import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { settingsApi } from "@/services/api";
import { DEFAULT_SETTINGS, CompanySettings } from "@/hooks/useCompanySettings";

// ── Mock quotation data (in real app, fetched by ID) ─────────────────────────
const mockQuotations = [
  {
    id: "QTN-2026-0045",
    type: "Quotation",
    date: "2026-04-01",
    validUntil: "2026-04-30",
    status: "Approved",
    client: {
      name: "Prestige Estates Pvt. Ltd.",
      gstin: "29AABCP9012H1Z8",
      address: "Prestige Tower, MG Road, Bengaluru - 560001",
      email: "projects@prestige.co.in",
      phone: "+91 80 2559 8765",
    },
    job: {
      jobNo: "HYD-SURV-2026-04-0008",
      title: "Topographic Survey — 50 Acre Township",
      location: "Gachibowli, Hyderabad",
    },
    items: [
      { description: "Topographic Survey (50 Acres)", qty: 1, rate: 125000, gstRate: 18 },
      { description: "Contour Mapping (1m interval)", qty: 1, rate: 45000, gstRate: 18 },
      { description: "Utility Mapping (underground)", qty: 1, rate: 65000, gstRate: 18 },
      { description: "GIS Database Creation", qty: 1, rate: 35000, gstRate: 18 },
      { description: "AutoCAD Drawings (A0 size)", qty: 5, rate: 2500, gstRate: 18 },
    ],
    notes: "Quotation valid for 30 days from date of issue. Prices are inclusive of all field and office work.",
    terms: "1. 50% advance payment required on order confirmation.\n2. Balance 50% on delivery of final drawings.\n3. Additional revisions beyond 2 iterations will be charged extra.\n4. All disputes subject to Hyderabad jurisdiction.",
  },
  {
    id: "QTN-2026-0044",
    type: "Quotation",
    date: "2026-03-25",
    validUntil: "2026-04-24",
    status: "Pending",
    client: {
      name: "Telangana State Housing Corp.",
      gstin: "36AABCT3456I1Z4",
      address: "TSHC Building, Masab Tank, Hyderabad - 500028",
      email: "info@tshc.gov.in",
      phone: "+91 40 2332 1100",
    },
    job: {
      jobNo: "HYD-SURV-2026-03-0015",
      title: "Land Demarcation — Housing Colony",
      location: "Shamshabad, Hyderabad",
    },
    items: [
      { description: "Boundary Demarcation (120 plots)", qty: 120, rate: 1200, gstRate: 18 },
      { description: "Pillar Fixing (RCC)", qty: 120, rate: 350, gstRate: 18 },
      { description: "Survey Report with Coordinates", qty: 1, rate: 15000, gstRate: 18 },
    ],
    notes: "Government tender rates applicable. GST extra as per invoice.",
    terms: "1. Work commencement within 15 days of PO.\n2. Payment within 30 days of invoice.\n3. TDS deduction as per government norms.",
  },
];

function numToWords(n: number): string {
  const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
    "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  if (n === 0) return "Zero";
  if (n < 20) return ones[n];
  if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : "");
  if (n < 1000) return ones[Math.floor(n / 100)] + " Hundred" + (n % 100 ? " " + numToWords(n % 100) : "");
  if (n < 100000) return numToWords(Math.floor(n / 1000)) + " Thousand" + (n % 1000 ? " " + numToWords(n % 1000) : "");
  if (n < 10000000) return numToWords(Math.floor(n / 100000)) + " Lakh" + (n % 100000 ? " " + numToWords(n % 100000) : "");
  return numToWords(Math.floor(n / 10000000)) + " Crore" + (n % 10000000 ? " " + numToWords(n % 10000000) : "");
}

export default function PrintQuotationPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [company, setCompany] = useState<CompanySettings>(DEFAULT_SETTINGS);
  useEffect(() => {
    settingsApi.getCompanyProfile().then((data) => {
      if (data) setCompany({ ...DEFAULT_SETTINGS, ...data });
    }).catch(() => {});
  }, []);

  // Find quotation by id param or default to first
  const quotation = mockQuotations.find((q) => q.id === id) || mockQuotations[0];

  const subtotal = quotation.items.reduce((acc, i) => acc + i.qty * i.rate, 0);

  const gstBreakdown = quotation.items.reduce((acc, item) => {
    const taxable = item.qty * item.rate;
    const gstAmt = (taxable * item.gstRate) / 100;
    const key = item.gstRate;
    if (!acc[key]) acc[key] = { taxable: 0, cgst: 0, sgst: 0, total: 0 };
    acc[key].taxable += taxable;
    acc[key].cgst += gstAmt / 2;
    acc[key].sgst += gstAmt / 2;
    acc[key].total += gstAmt;
    return acc;
  }, {} as Record<number, { taxable: number; cgst: number; sgst: number; total: number }>);

  const totalGST = Object.values(gstBreakdown).reduce((a, b) => a + b.total, 0);
  const grandTotal = subtotal + totalGST;

  const statusColors: Record<string, string> = {
    "Approved": "#16a34a",
    "Pending": "#d97706",
    "Rejected": "#dc2626",
    "Expired": "#6b7280",
  };
  const statusColor = statusColors[quotation.status] || "#6b7280";

  return (
    <>
      {/* ── Screen toolbar — hidden on print ── */}
      <div className="print:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/billing")}
            className="flex items-center gap-2 px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line text-sm" /></span>
            Back to Billing
          </button>
          <span className="text-sm font-semibold text-gray-700">Quotation: {quotation.id}</span>
          <span
            className="text-xs font-semibold px-2.5 py-0.5 rounded-full"
            style={{ background: `${statusColor}18`, color: statusColor }}
          >
            {quotation.status}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => window.print()}
            className="flex items-center gap-2 px-4 py-1.5 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 cursor-pointer whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-printer-line text-sm" /></span>
            Print / Save PDF
          </button>
        </div>
      </div>

      {/* ── A4 Page ── */}
      <div className="print:pt-0 pt-16 min-h-screen bg-gray-100 print:bg-white flex justify-center">
        <div
          className="bg-white w-full print:w-full print:shadow-none my-6 print:my-0"
          style={{ maxWidth: "210mm", minHeight: "297mm", padding: "18mm 20mm" }}
        >

          {/* ── HEADER BAND ── */}
          <div style={{ borderBottom: "3px solid #3b82f6", paddingBottom: "16px", marginBottom: "20px" }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "16px" }}>
              {/* Company Info */}
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                {company.logo && (
                  <img src={company.logo} alt="logo" style={{ width: "52px", height: "52px", objectFit: "contain" }} />
                )}
                <div>
                  <h1 style={{ fontSize: "15px", fontWeight: "800", color: "#111827", margin: 0 }}>{company.name}</h1>
                  <p style={{ fontSize: "10px", color: "#6b7280", margin: "2px 0 0" }}>{company.address}</p>
                  <p style={{ fontSize: "10px", color: "#6b7280", margin: "1px 0 0" }}>
                    {company.email} &nbsp;|&nbsp; {company.phone}
                  </p>
                  {company.gstin && (
                    <p style={{ fontSize: "10px", color: "#6b7280", margin: "1px 0 0" }}>GSTIN: {company.gstin}</p>
                  )}
                  {company.website && (
                    <p style={{ fontSize: "10px", color: "#6b7280", margin: "1px 0 0" }}>{company.website}</p>
                  )}
                </div>
              </div>
              {/* Quotation Badge */}
              <div style={{ textAlign: "right", flexShrink: 0 }}>
                <p style={{ fontSize: "22px", fontWeight: "900", color: "#3b82f6", margin: 0, letterSpacing: "-0.5px" }}>
                  QUOTATION
                </p>
                <p style={{ fontSize: "13px", fontWeight: "700", color: "#111827", fontFamily: "monospace", margin: "3px 0 2px" }}>
                  {quotation.id}
                </p>
                <p style={{ fontSize: "10px", color: "#6b7280", margin: "1px 0" }}>Date: {quotation.date}</p>
                <p style={{ fontSize: "10px", color: "#dc2626", margin: "1px 0", fontWeight: "600" }}>Valid Until: {quotation.validUntil}</p>
                <span style={{
                  display: "inline-block", marginTop: "4px",
                  fontSize: "10px", fontWeight: "700", padding: "2px 10px", borderRadius: "999px",
                  background: `${statusColor}18`, color: statusColor,
                }}>
                  {quotation.status}
                </span>
              </div>
            </div>
          </div>

          {/* ── CLIENT INFO ── */}
          <div style={{ marginBottom: "18px", padding: "12px", background: "#f9fafb", borderRadius: "8px", border: "1px solid #f3f4f6" }}>
            <p style={{ fontSize: "9px", fontWeight: "700", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "7px" }}>Prepared For</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
              <div>
                <p style={{ fontSize: "13px", fontWeight: "700", color: "#111827", margin: "0 0 3px" }}>{quotation.client.name}</p>
                <p style={{ fontSize: "10px", color: "#6b7280", margin: "0 0 2px" }}>GSTIN: {quotation.client.gstin}</p>
                <p style={{ fontSize: "10px", color: "#6b7280", margin: "0 0 2px" }}>{quotation.client.address}</p>
              </div>
              <div>
                <p style={{ fontSize: "10px", color: "#6b7280", margin: "0 0 2px" }}>{quotation.client.email}</p>
                <p style={{ fontSize: "10px", color: "#6b7280", margin: 0 }}>{quotation.client.phone}</p>
              </div>
            </div>
          </div>

          {/* ── JOB REFERENCE ── */}
          <div style={{ marginBottom: "18px", padding: "12px", background: "#eff6ff", borderRadius: "8px", border: "1px solid #bfdbfe" }}>
            <p style={{ fontSize: "9px", fontWeight: "700", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "7px" }}>Project Reference</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
              <div>
                <p style={{ fontSize: "12px", fontWeight: "700", color: "#1e40af", fontFamily: "monospace", margin: "0 0 4px" }}>{quotation.job.jobNo}</p>
                <p style={{ fontSize: "11px", fontWeight: "600", color: "#111827", margin: "0 0 3px" }}>{quotation.job.title}</p>
              </div>
              <div>
                <p style={{ fontSize: "10px", color: "#6b7280", margin: 0 }}>{quotation.job.location}</p>
              </div>
            </div>
          </div>

          {/* ── LINE ITEMS TABLE ── */}
          <div style={{ marginBottom: "16px" }}>
            <p style={{ fontSize: "9px", fontWeight: "700", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "8px" }}>Scope of Work & Pricing</p>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#1e40af" }}>
                  <th style={{ fontSize: "9px", fontWeight: "700", color: "#fff", padding: "7px 10px", textAlign: "left", borderRadius: "6px 0 0 0" }}>#</th>
                  <th style={{ fontSize: "9px", fontWeight: "700", color: "#fff", padding: "7px 10px", textAlign: "left" }}>Description of Services</th>
                  <th style={{ fontSize: "9px", fontWeight: "700", color: "#fff", padding: "7px 10px", textAlign: "center" }}>Qty</th>
                  <th style={{ fontSize: "9px", fontWeight: "700", color: "#fff", padding: "7px 10px", textAlign: "right" }}>Rate (₹)</th>
                  <th style={{ fontSize: "9px", fontWeight: "700", color: "#fff", padding: "7px 10px", textAlign: "center" }}>GST %</th>
                  <th style={{ fontSize: "9px", fontWeight: "700", color: "#fff", padding: "7px 10px", textAlign: "right", borderRadius: "0 6px 0 0" }}>Amount (₹)</th>
                </tr>
              </thead>
              <tbody>
                {quotation.items.map((item, idx) => (
                  <tr key={idx} style={{ borderBottom: "1px solid #f3f4f6", background: idx % 2 === 0 ? "#fff" : "#fafafa" }}>
                    <td style={{ fontSize: "10px", padding: "8px 10px", color: "#9ca3af" }}>{idx + 1}</td>
                    <td style={{ fontSize: "11px", padding: "8px 10px", color: "#111827", fontWeight: "500" }}>{item.description}</td>
                    <td style={{ fontSize: "11px", padding: "8px 10px", color: "#374151", textAlign: "center" }}>{item.qty}</td>
                    <td style={{ fontSize: "11px", padding: "8px 10px", color: "#374151", textAlign: "right" }}>
                      {item.rate.toLocaleString("en-IN")}
                    </td>
                    <td style={{ fontSize: "11px", padding: "8px 10px", color: "#374151", textAlign: "center" }}>{item.gstRate}%</td>
                    <td style={{ fontSize: "11px", padding: "8px 10px", color: "#111827", fontWeight: "700", textAlign: "right" }}>
                      {(item.qty * item.rate).toLocaleString("en-IN")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ── GST BREAKDOWN + TOTALS ── */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "16px" }}>
            {/* GST Summary */}
            <div>
              <p style={{ fontSize: "9px", fontWeight: "700", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "8px" }}>GST Summary</p>
              <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #e5e7eb", borderRadius: "8px", overflow: "hidden" }}>
                <thead>
                  <tr style={{ background: "#f3f4f6" }}>
                    <th style={{ fontSize: "9px", padding: "5px 8px", textAlign: "left", color: "#6b7280", fontWeight: "600" }}>Rate</th>
                    <th style={{ fontSize: "9px", padding: "5px 8px", textAlign: "right", color: "#6b7280", fontWeight: "600" }}>Taxable</th>
                    <th style={{ fontSize: "9px", padding: "5px 8px", textAlign: "right", color: "#6b7280", fontWeight: "600" }}>CGST</th>
                    <th style={{ fontSize: "9px", padding: "5px 8px", textAlign: "right", color: "#6b7280", fontWeight: "600" }}>SGST</th>
                    <th style={{ fontSize: "9px", padding: "5px 8px", textAlign: "right", color: "#6b7280", fontWeight: "600" }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(gstBreakdown).map(([rate, vals]) => (
                    <tr key={rate} style={{ borderTop: "1px solid #f3f4f6" }}>
                      <td style={{ fontSize: "10px", padding: "5px 8px", fontWeight: "600", color: "#374151" }}>{rate}%</td>
                      <td style={{ fontSize: "10px", padding: "5px 8px", textAlign: "right", color: "#374151" }}>₹{vals.taxable.toLocaleString("en-IN")}</td>
                      <td style={{ fontSize: "10px", padding: "5px 8px", textAlign: "right", color: "#374151" }}>₹{vals.cgst.toLocaleString("en-IN")}</td>
                      <td style={{ fontSize: "10px", padding: "5px 8px", textAlign: "right", color: "#374151" }}>₹{vals.sgst.toLocaleString("en-IN")}</td>
                      <td style={{ fontSize: "10px", padding: "5px 8px", textAlign: "right", fontWeight: "700", color: "#111827" }}>₹{vals.total.toLocaleString("en-IN")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
              <div style={{ background: "#f9fafb", borderRadius: "8px", border: "1px solid #e5e7eb", padding: "12px" }}>
                {[
                  { label: "Subtotal", value: `₹${subtotal.toLocaleString("en-IN")}`, bold: false },
                  { label: "Total GST", value: `₹${totalGST.toLocaleString("en-IN")}`, bold: false },
                ].map((row) => (
                  <div key={row.label} style={{ display: "flex", justifyContent: "space-between", marginBottom: "5px" }}>
                    <span style={{ fontSize: "11px", color: "#6b7280" }}>{row.label}</span>
                    <span style={{ fontSize: "11px", color: "#374151", fontWeight: "500" }}>{row.value}</span>
                  </div>
                ))}
                <div style={{ borderTop: "2px solid #3b82f6", marginTop: "6px", paddingTop: "6px", display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontSize: "13px", fontWeight: "800", color: "#111827" }}>Grand Total</span>
                  <span style={{ fontSize: "13px", fontWeight: "800", color: "#3b82f6" }}>₹{grandTotal.toLocaleString("en-IN")}</span>
                </div>
                <p style={{ fontSize: "9px", color: "#9ca3af", fontStyle: "italic", marginTop: "4px" }}>
                  {numToWords(Math.round(grandTotal))} Rupees Only
                </p>
              </div>
            </div>
          </div>

          {/* ── NOTES & TERMS ── */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "20px" }}>
            {quotation.notes && (
              <div style={{ padding: "10px 12px", background: "#fffbeb", borderRadius: "8px", border: "1px solid #fde68a" }}>
                <p style={{ fontSize: "9px", fontWeight: "700", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "5px" }}>Notes</p>
                <p style={{ fontSize: "10px", color: "#374151", lineHeight: "1.6", margin: 0 }}>{quotation.notes}</p>
              </div>
            )}
            {quotation.terms && (
              <div style={{ padding: "10px 12px", background: "#f9fafb", borderRadius: "8px", border: "1px solid #e5e7eb" }}>
                <p style={{ fontSize: "9px", fontWeight: "700", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "5px" }}>Terms &amp; Conditions</p>
                <p style={{ fontSize: "10px", color: "#374151", lineHeight: "1.6", margin: 0, whiteSpace: "pre-line" }}>{quotation.terms}</p>
              </div>
            )}
          </div>

          {/* ── BANK DETAILS ── */}
          <div style={{ marginBottom: "20px", padding: "10px 12px", background: "#eff6ff", borderRadius: "8px", border: "1px solid #bfdbfe" }}>
            <p style={{ fontSize: "9px", fontWeight: "700", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "6px" }}>Bank Details for Payment</p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "8px" }}>
              {[
                { label: "Bank Name", value: "State Bank of India" },
                { label: "Account No.", value: "XXXX XXXX 4567" },
                { label: "IFSC Code", value: "SBIN0020123" },
                { label: "Account Type", value: "Current Account" },
              ].map((b) => (
                <div key={b.label}>
                  <p style={{ fontSize: "9px", color: "#9ca3af", margin: "0 0 1px" }}>{b.label}</p>
                  <p style={{ fontSize: "10px", fontWeight: "600", color: "#1e40af", margin: 0 }}>{b.value}</p>
                </div>
              ))}
            </div>
          </div>

          {/* ── ACCEPTANCE SECTION ── */}
          <div style={{ marginBottom: "20px", padding: "12px", background: "#f0fdf4", borderRadius: "8px", border: "1px solid #bbf7d0" }}>
            <p style={{ fontSize: "9px", fontWeight: "700", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "8px" }}>Quotation Acceptance</p>
            <p style={{ fontSize: "10px", color: "#374151", lineHeight: "1.6", margin: "0 0 10px" }}>
              I/We hereby accept the above quotation and agree to the terms and conditions mentioned. Please proceed with the work as per the scope defined above.
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "16px" }}>
              <div>
                <p style={{ fontSize: "9px", color: "#9ca3af", margin: "0 0 2px" }}>Authorized Signatory</p>
                <div style={{ height: "32px", borderBottom: "1px solid #374151", marginBottom: "4px" }} />
                <p style={{ fontSize: "9px", color: "#6b7280", margin: 0 }}>Name &amp; Designation</p>
              </div>
              <div>
                <p style={{ fontSize: "9px", color: "#9ca3af", margin: "0 0 2px" }}>Company Stamp</p>
                <div style={{ height: "32px", borderBottom: "1px solid #374151", marginBottom: "4px" }} />
                <p style={{ fontSize: "9px", color: "#6b7280", margin: 0 }}>Official Seal</p>
              </div>
              <div>
                <p style={{ fontSize: "9px", color: "#9ca3af", margin: "0 0 2px" }}>Date</p>
                <div style={{ height: "32px", borderBottom: "1px solid #374151", marginBottom: "4px" }} />
                <p style={{ fontSize: "9px", color: "#6b7280", margin: 0 }}>DD/MM/YYYY</p>
              </div>
            </div>
          </div>

          {/* ── SIGNATURES ── */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px", marginTop: "24px" }}>
            {[
              { role: "Prepared By", name: company.name },
              { role: "Client Acknowledgement", name: quotation.client.name },
            ].map((sig) => (
              <div key={sig.role} style={{ textAlign: "center" }}>
                <div style={{ height: "44px", borderBottom: "1.5px solid #374151", marginBottom: "6px" }} />
                <p style={{ fontSize: "10px", fontWeight: "700", color: "#111827", margin: "0 0 2px" }}>{sig.name}</p>
                <p style={{ fontSize: "9px", color: "#6b7280", margin: "0 0 2px" }}>{sig.role}</p>
                <p style={{ fontSize: "9px", color: "#9ca3af", margin: 0 }}>Date: ___________</p>
              </div>
            ))}
          </div>

          {/* ── FOOTER ── */}
          <div style={{ marginTop: "20px", paddingTop: "10px", borderTop: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <p style={{ fontSize: "8px", color: "#9ca3af", margin: 0 }}>
              This is a computer-generated quotation. Valid until {quotation.validUntil}. | {company.name} | {company.email}
            </p>
            <p style={{ fontSize: "8px", color: "#9ca3af", margin: 0 }}>
              {quotation.id} | Page 1 of 1
            </p>
          </div>
        </div>
      </div>
    </>
  );
}